import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Avatar,
    Button,
    Divider,
    IconButton,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Close as CloseIcon,
    Group as GroupIcon,
    RequestPage as FundsRequestIcon,
    EmojiEvents as WelcomeIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import parse from 'html-react-parser';

const NotificationDialog = ({
    open,
    onClose,
    notification,
    onNotificationUpdate,
    onNotificationDelete
}) => {
    const navigate = useNavigate();
    const [relatedData, setRelatedData] = useState(null);
    const [relatedDataLoading, setRelatedDataLoading] = useState(false);
    const [relatedDataError, setRelatedDataError] = useState(null);

    const getNotificationIcon = (type) => {
        const icons = {
            'GROUP_REQUEST': <GroupIcon color="primary" />,
            'GROUP_UPDATE': <GroupIcon color="info" />,
            'FUNDS_REQUEST': <FundsRequestIcon color="warning" />,
            'CAMPAIGN_VERIFICATION_REQUEST': <FundsRequestIcon color="success" />,
            'WELCOME': <WelcomeIcon color="success" />
        };
        return icons[type] || <GroupIcon />;
    };

    const getNotificationColor = (type) => {
        const colors = {
            'GROUP_REQUEST': 'primary',
            'GROUP_UPDATE': 'info',
            'FUNDS_REQUEST': 'warning',
            'CAMPAIGN_VERIFICATION_REQUEST': 'success',
            'WELCOME': 'success'
        };
        return colors[type] || 'default';
    };

    const fetchRelatedData = async () => {
        if (!notification?.targetUuid) {
            setRelatedData(null);
            return;
        }

        setRelatedDataLoading(true);
        setRelatedDataError(null);

        try {
            let response;
            let endpoint;

            switch (notification.type) {
                case 'GROUP_REQUEST':
                case 'GROUP_UPDATE':
                    endpoint = `/v1/api/groups/${notification.targetUuid}`;
                    break;

                case 'FUNDS_REQUEST':
                    endpoint = `/v1/api/groups/group/funds-requests/${notification.targetUuid}`;
                    break;

                default:
                    setRelatedDataLoading(false);
                    return;
            }

            if (endpoint) {
                response = await api.get(endpoint);
                setRelatedData(response.data);
            }
        } catch (err) {
            setRelatedDataError('Failed to fetch additional information');
            console.error('Error fetching related data:', err);
        } finally {
            setRelatedDataLoading(false);
        }
    };

    const markAsRead = async () => {
        if (!notification || notification.read) return;

        try {
            await api.patch(`/v1/api/notifications/${notification.id}/read`);
            if (onNotificationUpdate) {
                onNotificationUpdate(notification.id, { read: true });
            }
        } catch (err) {
            console.error('Error marking notification as read:', err);
        }
    };

    const deleteNotification = async () => {
        if (!notification) return;

        try {
            await api.delete(`/v1/api/notifications/${notification.id}`);
            if (onNotificationDelete) {
                onNotificationDelete(notification.id);
            }
            onClose();
        } catch (err) {
            console.error('Error deleting notification:', err);
        }
    };

    const handleAction = () => {
        if (!notification) return;

        // Close dialog first
        onClose();

        // Navigate based on notification type
        switch (notification.type) {
            case 'GROUP_REQUEST':
            case 'GROUP_UPDATE':
                if (notification.targetUuid) {
                    navigate(`/groups/${notification.targetUuid}`);
                } else {
                    navigate('/groups/enrolled-groups');
                }
                break;
            case 'FUNDS_REQUEST':
                if (notification.targetUuid) {
                    navigate(`/groups/${notification.sourceUuid}/requests/${notification.targetUuid}`);
                } else {
                    navigate('/groups/requests');
                }
                break;
            default:
                navigate('/notifications');
                break;
        }
    };

    const renderRelatedData = () => {
        if (!notification) return null;

        if (relatedDataLoading) {
            return (
                <Box display="flex" justifyContent="center" my={3}>
                    <CircularProgress size={24} />
                </Box>
            );
        }

        if (relatedDataError) {
            return (
                <Alert severity="error" sx={{ my: 2 }}>
                    {relatedDataError}
                </Alert>
            );
        }

        if (!relatedData) return null;

        switch (notification.type) {
            case 'GROUP_REQUEST':
            case 'GROUP_UPDATE':
                return (
                    <Box mt={3}>
                        <Typography variant="subtitle1" gutterBottom>
                            <GroupIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Group Details
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            p: 2,
                            borderLeft: '4px solid',
                            borderColor: notification.type === 'GROUP_REQUEST' ? 'primary.main' : 'info.main'
                        }}>
                            <Typography><strong>Name:</strong> {relatedData.name}</Typography>
                            <Typography><strong>Description:</strong> {parse(relatedData.description || relatedData.groupShortDescription || '')}</Typography>
                            <Typography><strong>Status:</strong>
                                <Chip
                                    label={relatedData.status || relatedData.groupStatus}
                                    size="small"
                                    sx={{ ml: 1 }}
                                    color={
                                        relatedData.status === 'ACTIVE' ? 'success' :
                                            relatedData.status === 'PENDING' ? 'warning' : 'default'
                                    }
                                />
                            </Typography>
                            {relatedData.createdAt && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Created: {format(new Date(relatedData.createdAt), 'PPpp')}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );

            case 'FUNDS_REQUEST':
                return (
                    <Box mt={3}>
                        <Typography variant="subtitle1" gutterBottom>
                            <FundsRequestIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Funds Request Details
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            p: 2,
                            borderLeft: '4px solid',
                            borderColor: 'warning.main'
                        }}>
                            <Typography><strong>Amount:</strong> ${relatedData.requestAmount?.toLocaleString() || '0'}</Typography>
                            <Typography><strong>Purpose:</strong> {relatedData.purpose || 'N/A'}</Typography>
                            <Typography><strong>Submitted:</strong> {format(new Date(relatedData.createdAt), 'PPpp') || 'N/A'}</Typography>
                            <Typography><strong>Due Date:</strong> {format(new Date(relatedData.dueDate), 'PPpp') || 'N/A'}</Typography>
                            <Typography><strong>Requestor:</strong> {relatedData.requestor || 'N/A'}</Typography>
                            <Typography><strong>Benefactor:</strong> {relatedData.recipient || 'N/A'}</Typography>
                            <Typography><strong>Request Title:</strong> {relatedData.requestTitle || 'N/A'}</Typography>
                            <Typography><strong>Request Description:</strong> {parse(relatedData.requestDescription) || 'N/A'}</Typography>

                            <Box mt={2}>
                                <Typography variant="caption" color="text.secondary">
                                    Source: {notification.source}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                );

            default:
                return null;
        }
    };

    // Fetch related data when notification changes
    useEffect(() => {
        if (notification && open) {
            fetchRelatedData();
            markAsRead();
        }
    }, [notification, open]);

    if (!notification) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2
                }
            }}
        >
            <DialogTitle sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                py: 2,
                px: 3
            }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{
                            bgcolor: 'background.paper',
                            width: 48,
                            height: 48
                        }}>
                            {getNotificationIcon(notification.type)}
                        </Avatar>
                        <Box flex={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Typography variant="h6" fontWeight="bold" sx={{mr: 1}}>
                                    {notification.title}
                                </Typography>
                                <Chip
                                    label={notification.type.replace(/_/g, ' ')}
                                    size="small"
                                    color={getNotificationColor(notification.type)}
                                />
                            </Box>
                            <Typography  variant="body2" color="text.secondary">
                                {format(new Date(notification.createdAt), 'PPpp')}
                            </Typography>
                        </Box>
                    </Box>


                    <IconButton
                        onClick={onClose}
                        sx={{ color: 'inherit' }}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    
                    <Box>
                        <Typography variant="body1" paragraph sx={{
                            whiteSpace: "pre-line",
                            lineHeight: 2.2
                        }}>
                            Dear user,{"\n"}{notification.message}
                        </Typography>
                    </Box>

                    {/* Related Data */}
                    {renderRelatedData()}

                    {/* Footer */}
                    <Box mt={2}>
                        <Typography variant="caption" sx={{ fontStyle: 'italic', mb: '1px' }}>
                            Best regards,
                        </Typography>
                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                            Uniofunds System Administrator
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{
                p: 2,
                justifyContent: 'space-between',
                borderTop: 1,
                borderColor: 'divider'
            }}>
                <Button
                    onClick={deleteNotification}
                    color="error"
                    startIcon={<DeleteIcon />}
                    size="small"
                >
                    Delete
                </Button>
                <Box>
                    <Button
                        onClick={onClose}
                        sx={{ mr: 1 }}
                        size="small"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={handleAction}
                        variant="contained"
                        size="small"
                    >
                        {notification.type === 'GROUP_REQUEST' || notification.type === 'GROUP_UPDATE'
                            ? 'View Group'
                            : notification.type === 'FUNDS_REQUEST'
                                ? 'View Request'
                                : 'View Details'
                        }
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default NotificationDialog;